import {
    type PropsWithChildren,
    createContext,
    useContext,
    useMemo
} from "react";

import {
    EnrollmentType,
    getExperiment
} from "@/utils/experiment/use-experiment";
import {
    wacRefreshConfluence,
    wacRefreshHomepage,
    wacRefreshJira,
    wacRefreshTwcRovo
} from "@/utils/experiment/wac-refresh-experiments";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

/**
 * Context value for the WAC Refresh Q4 FY26 feature gate + experiment.
 * The check is performed once at the provider level and memoized so all
 * descendant consumers receive the same stable boolean without re-running
 * the gate/experiment logic on every render.
 */
type WacRefreshContextValue = {
    isWacRefresh: boolean;
};

const WacRefreshContext = createContext<WacRefreshContextValue>({
    isWacRefresh: false
});

/**
 * Provides the `isWacRefresh` flag to the component tree.
 */
export const WacRefreshProvider = ({
    isWacRefresh: isWacRefreshOverride,
    children
}: PropsWithChildren<{ isWacRefresh?: boolean }>) => {
    const isWACRefreshGateEnabled = checkFeatureFlagGate(
        featureFlags.wacRefreshTeam26Gate
    );

    // Get the experiments without firing exposures.
    // Exposures are fired in the Uniform experiment container (Jira, Confluence, TWC, Rovo)
    // and in get-route.tsx (Homepage)
    const jiraExperiment = getExperiment(wacRefreshJira);
    const confluenceExperiment = getExperiment(wacRefreshConfluence);
    const homepageExperiment = getExperiment(wacRefreshHomepage);
    const twcRovoExperiment = getExperiment(wacRefreshTwcRovo);

    const wacRefreshExperimentEnabled =
        (jiraExperiment.type === EnrollmentType.ENROLLED &&
            jiraExperiment.value) ||
        (confluenceExperiment.type === EnrollmentType.ENROLLED &&
            confluenceExperiment.value) ||
        (homepageExperiment.type === EnrollmentType.ENROLLED &&
            homepageExperiment.value) ||
        (twcRovoExperiment.type === EnrollmentType.ENROLLED &&
            twcRovoExperiment.value);

    const value = useMemo<WacRefreshContextValue>(
        () => ({
            isWacRefresh:
                isWacRefreshOverride ??
                (isWACRefreshGateEnabled || wacRefreshExperimentEnabled)
        }),
        [
            isWacRefreshOverride,
            isWACRefreshGateEnabled,
            wacRefreshExperimentEnabled
        ]
    );

    return (
        <WacRefreshContext.Provider value={value}>
            {children}
        </WacRefreshContext.Provider>
    );
};

/**
 * Returns `true` when the WAC Refresh Q4 FY26 feature gate is enabled.
 *
 * Reads from context — defaults to `false` when used outside of a
 * `<WacRefreshProvider>` (e.g. in unit tests that render components in
 * isolation).
 */
export const useWacRefresh = (): boolean => {
    const { isWacRefresh } = useContext(WacRefreshContext);
    return isWacRefresh;
};
